import { eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useLocalSearchParams } from 'expo-router'
import { useCallback, useMemo, useState } from 'react'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useOrgBySlug } from '@/hooks/useOrgBySlug'
import { useAuth } from '@/lib/auth'
import { captureException } from '@/lib/errors'
import { performMutations, useMutation } from '@/lib/mutations'
import { useStore } from '@/lib/pocketbase'
import type { Users } from '@/types/pbSchema'
import type { DeleteDialogState, Member, MembersData, UserRole } from '@/types/people'
import { useInviteDialog } from './useInviteDialog'
import { useRoleDialog } from './useRoleDialog'
import { MOCK_MEMBERS } from './utils'

export { useInviteDialog, useRoleDialog }

export function useMembers(orgSlug?: string) {
    const { user } = useAuth()
    const params = useLocalSearchParams<{ orgSlug: string }>()
    const slug = orgSlug ?? params.orgSlug

    const [usersCollection, userOrgCollection, orgsCollection] = useStore(
        'users',
        'user_org',
        'orgs'
    )

    const { data: userOrgs = [], isLoading: listMemberLoading } = useLiveQuery(
        q =>
            slug
                ? q
                      .from({ org: orgsCollection })
                      .where(({ org }) => eq(org.slug, slug))
                      .join({ user_org: userOrgCollection }, ({ org, user_org }) =>
                          eq(user_org.org, org.id)
                      )
                      .join({ users: usersCollection }, ({ user_org, users }) =>
                          eq(user_org?.user ?? '', users.id)
                      )
                      .orderBy(({ users }) => users?.created ?? '', 'asc')
                      .select(({ users, user_org }) => ({ user: users, userOrg: user_org }))
                : undefined,
        [slug]
    )

    const { data: currentUserList = [], isLoading: getCurrentUserLoading } = useLiveQuery(
        q =>
            user?.id
                ? q.from({ users: usersCollection }).where(({ users }) => eq(users.id, user.id))
                : undefined,
        [user?.id]
    )

    const currentUser = currentUserList[0] ?? null

    const fallbackMembers: MembersData = useMemo(
        () => ({
            members: MOCK_MEMBERS as unknown as Member[],
            total: MOCK_MEMBERS.length,
        }),
        []
    )

    const members: MembersData = useMemo(() => {
        if (userOrgs && userOrgs.length > 0) {
            const membersList: Member[] = userOrgs
                .filter(
                    (
                        r
                    ): r is typeof r & {
                        user: NonNullable<typeof r.user>
                        userOrg: NonNullable<typeof r.userOrg>
                    } => Boolean(r.user && r.userOrg)
                )
                .map(r => {
                    const userOrg = r.userOrg as typeof r.userOrg & {
                        working_hours_override?: boolean
                        work_start_time?: string
                        work_end_time?: string
                        working_days?: number[]
                        color?: string
                        tracking_paused_until?: string
                        inactive_at?: string
                        supervisor?: string
                        clockin_reminders_disabled?: boolean
                    }
                    return {
                        id: r.user.id ?? '',
                        userId: r.user.id ?? '',
                        userOrgId: r.userOrg.id ?? '',
                        role: r.userOrg.role as UserRole,
                        createdAt: new Date(r.user.created || ''),
                        user: r.user as Users,
                        workingHoursOverride: userOrg.working_hours_override ?? false,
                        workStartTime: userOrg.work_start_time || null,
                        workEndTime: userOrg.work_end_time || null,
                        workingDays: userOrg.working_days ?? null,
                        color: userOrg.color || null,
                        trackingPausedUntil: userOrg.tracking_paused_until || null,
                        inactiveAt: userOrg.inactive_at || null,
                        supervisor: userOrg.supervisor || null,
                        clockinRemindersDisabled: userOrg.clockin_reminders_disabled ?? false,
                    }
                })

            return {
                members: membersList,
                total: membersList.length,
            }
        }
        return fallbackMembers
    }, [userOrgs, fallbackMembers])

    return {
        membership: currentUser,
        members,
        isLoading: getCurrentUserLoading || listMemberLoading,
    }
}

export function usePopoverState() {
    const [openPopover, setOpenPopover] = useState<string | null>(null)

    const openPopoverForMember = useCallback((memberId: string) => {
        setOpenPopover(memberId)
    }, [])

    const closePopover = useCallback(() => {
        setOpenPopover(null)
    }, [])

    const togglePopover = useCallback((memberId: string, open: boolean) => {
        setOpenPopover(open ? memberId : null)
    }, [])

    const isPopoverOpen = useCallback(
        (memberId: string) => {
            return openPopover === memberId
        },
        [openPopover]
    )

    return {
        openPopover,
        openPopoverForMember,
        closePopover,
        togglePopover,
        isPopoverOpen,
    }
}

export function useDeleteDialog() {
    const { org: currentOrg } = useCurrentOrg()
    const [usersCollection] = useStore('users')
    const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
        open: false,
        state: null,
    })

    const deleteMutation = useMutation({
        mutationFn: async ({ userId, userName }: { userId: string; userName: string }) => {
            await performMutations(function* () {
                yield usersCollection.delete(userId)
            })
            return { userName }
        },
        onSuccess: data => {
            alert(`User ${data.userName} has been removed from the organization.`)
        },
        onError: error => {
            captureException('Remove user from organization', error)
            alert('Failed to remove user. Please try again.')
        },
    })

    const openDeleteDialog = useCallback(
        (userId: string, userName: string, organizationId?: string) => {
            const orgId = organizationId || currentOrg.id
            setDeleteDialog({
                open: true,
                state: { userId, userName, organizationId: orgId },
            })
        },
        [currentOrg?.id]
    )

    const closeDeleteDialog = useCallback(() => {
        setDeleteDialog({ open: false, state: null })
    }, [])

    const confirmDelete = useCallback(async () => {
        if (!deleteDialog.state) return

        closeDeleteDialog()

        const { userId, userName } = deleteDialog.state
        await deleteMutation.mutateAsync({ userId, userName })
    }, [deleteDialog.state, closeDeleteDialog, deleteMutation])

    return {
        deleteDialog,
        openDeleteDialog,
        closeDeleteDialog,
        confirmDelete,
        isDeleting: deleteMutation.isPending,
    }
}

export function useMemberPermissions(orgSlug?: string) {
    const { user: currentUser } = useAuth()
    const params = useLocalSearchParams<{ orgSlug: string }>()
    const slug = orgSlug ?? params.orgSlug
    const { role: currentUserRole } = useOrgBySlug(slug)

    const canDeleteMember = useCallback(
        (member: Member) => {
            if (member.role === 'admin') {
                return false
            }

            if (currentUser && member.user.id === currentUser.id) {
                return false
            }

            if (currentUserRole !== 'admin') {
                return false
            }

            return true
        },
        [currentUser, currentUserRole]
    )

    const canUpdateMemberRole = useCallback(
        (member: Member) => {
            if (currentUser && member.user.id === currentUser.id) {
                return false
            }

            if (currentUserRole !== 'admin') {
                return false
            }

            return true
        },
        [currentUser, currentUserRole]
    )

    return {
        canDeleteMember,
        canUpdateMemberRole,
        currentUserRole,
    }
}

export function useMemberActions() {
    const { org: currentOrg } = useCurrentOrg()
    const { closePopover } = usePopoverState()
    const { openDeleteDialog } = useDeleteDialog()
    const { openRoleDialog } = useRoleDialog()

    const handleDeleteUser = useCallback(
        (userId: string, userName: string, organizationId?: string) => {
            closePopover()
            openDeleteDialog(userId, userName, organizationId)
        },
        [closePopover, openDeleteDialog]
    )

    const handleUpdateRole = useCallback(
        (userId: string, userName: string, currentRole: string) => {
            closePopover()
            openRoleDialog({
                memberId: userId,
                userName,
                organizationId: currentOrg.id,
                role: currentRole as UserRole,
            })
        },
        [closePopover, openRoleDialog, currentOrg]
    )

    return {
        handleDeleteUser,
        handleUpdateRole,
    }
}
